import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { CheckCircle, Calendar, User, Users, Shield } from 'lucide-react';
import { MONTHS_RO } from './constants';

export const BulkValidationView = ({ entries, users, year, selected, setSelected, techId, setTechId, techName, setTechName, dateMode, setDateMode, customDate, setCustomDate, bulkMonth, setBulkMonth, onValidate, onBulkUpdate, validating, bulkAction, setBulkAction, statusFilter, setStatusFilter, operatorName, setOperatorName, newStatus, setNewStatus, dateField, setDateField }) => {
  const months = Array.from({length: 12}, (_, i) => i + 1);

  const toggleEntry = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    if (selected.length === entries.length) setSelected([]);
    else setSelected(entries.map(e => e.id));
  };

  const ACTIONS = [
    { value: 'validate', label: 'Validare in masa', icon: CheckCircle, color: 'text-emerald-600' },
    { value: 'change_date', label: 'Schimba data', icon: Calendar, color: 'text-blue-600' },
    { value: 'change_technician', label: 'Schimba tehnician', icon: User, color: 'text-purple-600' },
    { value: 'change_operator', label: 'Schimba operator', icon: Users, color: 'text-orange-600' },
    { value: 'change_status', label: 'Schimba status', icon: Shield, color: 'text-slate-600' },
    { value: 'clear_data', label: 'Sterge date (reset)', icon: Shield, color: 'text-red-600' },
  ];
  const activeAction = ACTIONS.find(a => a.value === bulkAction) || ACTIONS[0];

  const canExecute = () => {
    if (selected.length === 0) return false;
    if (bulkAction === 'validate') return !!techId;
    if (bulkAction === 'change_date') return !!customDate;
    if (bulkAction === 'change_technician') return !!techId;
    if (bulkAction === 'change_operator') return true;
    if (bulkAction === 'change_status') return !!newStatus;
    if (bulkAction === 'clear_data') return true;
    return false;
  };

  const handleExecute = () => {
    if (bulkAction === 'validate') onValidate();
    else onBulkUpdate();
  };

  const actionLabel = () => {
    if (validating) return 'Se proceseaza...';
    const count = selected.length;
    if (bulkAction === 'validate') return `Valideaza ${count} intrari`;
    if (bulkAction === 'change_date') return `Actualizeaza data (${count})`;
    if (bulkAction === 'change_technician') return `Actualizeaza tehnician (${count})`;
    if (bulkAction === 'change_operator') return `Actualizeaza operator (${count})`;
    if (bulkAction === 'change_status') return `Schimba status (${count})`;
    if (bulkAction === 'clear_data') return `Sterge date (${count})`;
    return `Aplica (${count})`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="bulk-view">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
        <Shield className="w-5 h-5 text-blue-600" /> Operatiuni Bulk - {year}
      </h2>

      {/* Filters Row */}
      <div className="bg-white rounded-xl border p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Actiune</Label>
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="h-8 text-xs" data-testid="bulk-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIONS.map(a => (
                  <SelectItem key={a.value} value={a.value}>
                    <span className="flex items-center gap-1.5"><a.icon className={`w-3.5 h-3.5 ${a.color}`} />{a.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs" data-testid="bulk-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate</SelectItem>
                <SelectItem value="pending">Nevalidate</SelectItem>
                <SelectItem value="validated">Validate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] text-slate-500 uppercase font-semibold">Luna</Label>
            <Select value={bulkMonth || 'all'} onValueChange={v => setBulkMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="bulk-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate lunile</SelectItem>
                {months.map(m => <SelectItem key={m} value={String(m)}>{MONTHS_RO[m-1]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <div className="h-8 flex items-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{entries.length}</span> intrari incarcate
              {selected.length > 0 && <Badge variant="secondary" className="text-[10px]">{selected.length} selectate</Badge>}
            </div>
          </div>
        </div>

        {/* Action-specific config */}
        <div className="border-t pt-3">
          {bulkAction === 'validate' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Tehnician Responsabil</Label>
                <Select value={techId || '__none__'} onValueChange={v => {
                  if (v === '__none__') { setTechId(''); setTechName(''); return; }
                  setTechId(v);
                  const u = users.find(u => u.id === v);
                  setTechName(u?.full_name || '');
                }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-tech-select"><SelectValue placeholder="Selectati..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- Selectati --</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Data validare</Label>
                <Select value={dateMode} onValueChange={setDateMode}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-date-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Aleatorie (Luni-Vineri)</SelectItem>
                    <SelectItem value="custom">Data specifica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {dateMode === 'custom' && (
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase font-semibold">Data specifica</Label>
                  <Input className="h-8 text-xs" value={customDate} onChange={e => setCustomDate(e.target.value)}
                    placeholder="zz.ll.aaaa" data-testid="bulk-custom-date" />
                </div>
              )}
            </div>
          )}

          {bulkAction === 'change_date' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Data noua</Label>
                <Input className="h-8 text-xs" value={customDate} onChange={e => setCustomDate(e.target.value)}
                  placeholder="zz.ll.aaaa" data-testid="bulk-new-date" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Aplica pe</Label>
                <Select value={dateField} onValueChange={setDateField}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-date-field"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Data intretinere + verificare</SelectItem>
                    <SelectItem value="data_intretinere">Doar data intretinere</SelectItem>
                    <SelectItem value="data_verificare">Doar data verificare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {bulkAction === 'change_technician' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Tehnician nou</Label>
                <Select value={techId || '__none__'} onValueChange={v => {
                  if (v === '__none__') { setTechId(''); setTechName(''); return; }
                  setTechId(v);
                  const u = users.find(u => u.id === v);
                  setTechName(u?.full_name || '');
                }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-tech-select-update"><SelectValue placeholder="Selectati..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- Selectati --</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {bulkAction === 'change_operator' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Operator nou</Label>
                <Select value={operatorName || '__line__'} onValueChange={v => setOperatorName(v === '__line__' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-operator-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__line__">--- (linie goala)</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.full_name}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {bulkAction === 'change_status' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase font-semibold">Status nou</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="h-8 text-xs" data-testid="bulk-new-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Nevalidat (pending)</SelectItem>
                    <SelectItem value="validated">Validat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newStatus === 'pending' && (
                <div className="flex items-end">
                  <p className="text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1">Se vor reseta: data verificare, tehnician, semnatura</p>
                </div>
              )}
            </div>
          )}

          {bulkAction === 'clear_data' && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <p className="text-sm font-medium text-red-700 mb-2">Sterge urmatoarele campuri pentru intrarile selectate:</p>
              <ul className="text-xs text-red-600 space-y-1 list-disc list-inside">
                <li>Status → Nevalidat (pending)</li>
                <li>Data intretinere → gol</li>
                <li>Data verificare → gol</li>
                <li>Responsabil mentenanta → gol</li>
                <li>Semnatura responsabil → gol</li>
                <li>Operator utilaj → gol</li>
              </ul>
              <p className="text-[10px] text-red-500 mt-2 italic">Aceasta actiune nu poate fi anulata!</p>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-3">
            <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline" data-testid="bulk-toggle-all">
              {selected.length === entries.length && entries.length > 0 ? 'Deselecteaza toate' : 'Selecteaza toate'}
            </button>
            <span className="text-xs text-slate-500">{selected.length} din {entries.length} selectate</span>
          </div>
          <Button size="sm" className={`h-8 text-xs gap-1 ${bulkAction === 'change_status' && newStatus === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : ''} ${bulkAction === 'clear_data' ? 'bg-red-600 hover:bg-red-700' : ''}`}
            onClick={handleExecute} disabled={validating || !canExecute()} data-testid="bulk-execute-btn">
            <activeAction.icon className="w-3.5 h-3.5" /> {actionLabel()}
          </Button>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 w-8"><input type="checkbox" checked={selected.length === entries.length && entries.length > 0} onChange={toggleAll} className="rounded" /></th>
                <th className="text-left p-2 font-semibold text-slate-600">Utilaj</th>
                <th className="text-left p-2 font-semibold text-slate-600">Cod</th>
                <th className="text-left p-2 font-semibold text-slate-600">Sectie</th>
                <th className="text-left p-2 font-semibold text-slate-600">Luna</th>
                <th className="text-left p-2 font-semibold text-slate-600">Tip</th>
                <th className="text-left p-2 font-semibold text-slate-600">Status</th>
                <th className="text-left p-2 font-semibold text-slate-600">Responsabil</th>
                <th className="text-left p-2 font-semibold text-slate-600">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map(e => (
                <tr key={e.id} className={`hover:bg-slate-50 cursor-pointer ${selected.includes(e.id) ? 'bg-blue-50' : ''}`} onClick={() => toggleEntry(e.id)}>
                  <td className="p-2" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleEntry(e.id)} className="rounded" /></td>
                  <td className="p-2 font-medium text-slate-800">{e.equipment_name}</td>
                  <td className="p-2 text-slate-500 font-mono text-[10px]">{e.equipment_code}</td>
                  <td className="p-2 text-slate-600">{e.location_name || e.plant_name}</td>
                  <td className="p-2 text-slate-600">{MONTHS_RO[e.month - 1]}</td>
                  <td className="p-2 text-slate-600">{e.maintenance_type_name}</td>
                  <td className="p-2">
                    <Badge variant={e.status === 'validated' ? 'default' : 'secondary'}
                      className={`text-[10px] ${e.status === 'validated' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}`}>
                      {e.status === 'validated' ? 'Validat' : 'Nevalidat'}
                    </Badge>
                  </td>
                  <td className="p-2 text-slate-600 text-[10px]">{e.responsabil_mentenanta_name || '---'}</td>
                  <td className="p-2 text-slate-600 text-[10px]">{e.data_intretinere || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && <p className="text-center py-8 text-slate-400 text-sm">Nicio intrare gasita</p>}
        </div>
      </div>
    </div>
  );
};
